import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { RegenerateSelectionForm } from "@/components/shared/RegenerateSelectionForm";

interface RegeneratePopoverProps {
  documentId: string;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onRegenerated?: () => void;
}

interface PopoverState {
  visible: boolean;
  x: number;
  y: number;
  selectedText: string;
}

export function RegeneratePopover({ documentId, containerRef, onRegenerated }: RegeneratePopoverProps) {
  const [popover, setPopover] = useState<PopoverState>({ visible: false, x: 0, y: 0, selectedText: "" });
  const [showForm, setShowForm] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ verified: boolean; reason: string } | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const handleMouseUp = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !containerRef.current) {
      return;
    }

    const range = selection.getRangeAt(0);
    if (!containerRef.current.contains(range.commonAncestorContainer)) {
      return;
    }

    const text = selection.toString().trim();
    if (!text || text.length < 10) {
      return;
    }

    const rect = range.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    setPopover({
      visible: true,
      x: containerRef.current.offsetLeft + rect.left - containerRect.left + rect.width / 2,
      y: containerRef.current.offsetTop + rect.bottom - containerRect.top + 8,
      selectedText: text,
    });
    setShowForm(false);
    setInstruction("");
    setError(null);
    setResult(null);
  }, [containerRef]);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
      setPopover((prev) => ({ ...prev, visible: false }));
      setShowForm(false);
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    container.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      container.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [containerRef, handleMouseUp, handleMouseDown]);

  const handleRegenerate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const { api } = await import("@/lib/api");
      const response = await api.documents.regenerate(documentId, {
        selected_text: popover.selectedText,
        instruction,
        case_context: "",
        source_spans: null,
      });
      setResult({ verified: response.verified, reason: response.verification_reason });
      if (response.ok) {
        onRegenerated?.();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Regeneration failed.");
    } finally {
      setLoading(false);
    }
  };

  if (!popover.visible) {
    return null;
  }

  return (
    <div
      ref={popoverRef}
      className="absolute z-50 w-72 rounded-md border bg-card p-3 shadow-md"
      style={{ left: popover.x, top: popover.y, transform: "translateX(-50%)" }}
    >
      {!showForm && !result ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-2"
          onClick={() => setShowForm(true)}
        >
          <RefreshCw className="size-4" />
          Edit with assistant
        </Button>
      ) : null}

      {showForm && !result ? (
        <RegenerateSelectionForm
          selectedText={popover.selectedText}
          instruction={instruction}
          onInstructionChange={setInstruction}
          placeholder="Describe the change: e.g. 'make it more specific'"
          submitLabel="Apply edit"
          loadingLabel="Verifying…"
          loading={loading}
          error={error}
          onCancel={() => {
            setPopover((prev) => ({ ...prev, visible: false }));
            setShowForm(false);
          }}
          onSubmit={() => void handleRegenerate()}
        />
      ) : null}

      {result ? (
        <div className="space-y-2">
          <p className="text-xs font-medium">
            {result.verified ? (
              <span className="text-success">✓ Verified</span>
            ) : (
              <span className="text-destructive">✗ Unverified</span>
            )}
          </p>
          <p className="text-xs text-muted-foreground">{result.reason}</p>
          <p className="text-xs text-muted-foreground">
            The regenerated text has been applied as a tracked change. Accept or reject it below.
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => setPopover((prev) => ({ ...prev, visible: false }))}
          >
            Done
          </Button>
        </div>
      ) : null}
    </div>
  );
}
